import loadScript from "./loadScript.js";
import loadSharedModule from "./loadSharedModule.js";
let resolveBasicPkg;
let rejectBasicPkg;
let basicPkgWillBeResolved = false;
const waitBasicPkg = new Promise((resolve, reject) => {
  resolveBasicPkg = resolve;
  rejectBasicPkg = reject;
});
export function flushStableLoadBricks() {
  if (!basicPkgWillBeResolved) {
    resolveBasicPkg();
  }
}

/**
 * When loading bundles with webpack module federation concurrently, if
 * these bundles share some modules, webpack will load a singleton module
 * if versions are matched. Webpack will use the first bundle who started
 * to init the shared scope. Generally which bundle to use for a specific
 * module, does not matter. However, it may cause flaky result since the
 * shared module may from different bundles which maybe not exactly the
 * same, especially developers declare dependencies incorrectly sometimes.
 * So in order to make it less flaky, we try to make a BASIC package takes
 * precedence over others. We will always load the shared modules from the
 * basic package bundle if it contains the shared modules.
 */
export function enqueueStableLoadBricks(bricks, brickPackages) {
  const promise = enqueueStableLoad("bricks", bricks, brickPackages);
  return dispatchRequestStatus(promise);
}
export function enqueueStableLoadProcessors(processors, brickPackages) {
  const promise = enqueueStableLoad("processors", processors, brickPackages);
  return dispatchRequestStatus(promise);
}
export async function loadBricksImperatively(bricks, brickPackages,
// Post loading process hook with all v2 bricks including dependencies
postProcessV2Bricks) {
  const promise = enqueueStableLoad("bricks", bricks, brickPackages, postProcessV2Bricks);
  flushStableLoadBricks();
  return dispatchRequestStatus(promise);
}
export function loadProcessorsImperatively(processors, brickPackages) {
  const promise = enqueueStableLoad("processors", processors, brickPackages);
  flushStableLoadBricks();
  return dispatchRequestStatus(promise);
}
export function loadEditorsImperatively(editors, brickPackages) {
  const promise = enqueueStableLoad("editors", editors, brickPackages);
  flushStableLoadBricks();
  return dispatchRequestStatus(promise);
}
export class BrickLoadError extends Error {
  constructor(message) {
    // Pass remaining arguments (including vendor specific ones) to parent constructor
    super(message);
    this.name = "BrickLoadError";

    // Maintains proper stack trace for where our error was thrown (only available on V8)
    // istanbul ignore else
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, BrickLoadError);
    }
  }
}
let v2AdapterPromise;
const V2_ADAPTER_LOAD_BRICKS = "v2-adapter.load-bricks";
// Get brick/processor items including their dependencies
function getItemsByPkg(type, list, brickPackagesMap) {
  const itemsByPkg = new Map();
  const listToLoad = new Set();
  const add = item => {
    var _pkg$dependencies;
    if (listToLoad.has(item)) {
      return;
    }
    listToLoad.add(item);
    let pkg;
    let namespace;
    let lastName;
    if (type === "editors") {
      lastName = item;
      for (const p of brickPackagesMap.values()) {
        var _ref, _p$propertyEditors;
        if ((_ref = (_p$propertyEditors = p.propertyEditors) !== null && _p$propertyEditors !== void 0 ? _p$propertyEditors : p.editors) !== null && _ref !== void 0 && _ref.some(e => e === lastName)) {
          pkg = p;
        }
      }
    } else if (type === "processors" || item.includes(".")) {
      [namespace, lastName] = item.split(".");
      const pkgId = `bricks/${type === "processors" ? getProcessorPackageName(namespace) : namespace}`;
      pkg = brickPackagesMap.get(pkgId);
    } else {
      lastName = item;
      let deprecatedBrickInThisPkg;
      for (const p of brickPackagesMap.values()) {
        var _p$elements;
        if ((_p$elements = p.elements) !== null && _p$elements !== void 0 && _p$elements.some(e => e === lastName)) {
          var _p$deprecatedElements;
          if ((_p$deprecatedElements = p.deprecatedElements) !== null && _p$deprecatedElements !== void 0 && _p$deprecatedElements.includes(item)) {
            deprecatedBrickInThisPkg = p;
          } else {
            pkg = p;
            break;
          }
        }
      }
      if (!pkg && deprecatedBrickInThisPkg) {
        pkg = deprecatedBrickInThisPkg;
      }
    }
    if (!pkg) {
      // eslint-disable-next-line no-console
      console.error(`Package for ${item} not found.`);
      return;
    }
    let groupItems = itemsByPkg.get(pkg);
    if (!groupItems) {
      groupItems = [];
      itemsByPkg.set(pkg, groupItems);
    }
    groupItems.push({
      fullName: item,
      lastName
    });

    // Load their dependencies too
    const deps = (_pkg$dependencies = pkg.dependencies) === null || _pkg$dependencies === void 0 ? void 0 : _pkg$dependencies[item];
    if (deps) {
      for (const dep of deps) {
        add(dep);
      }
    }
  };
  for (const item of list) {
    add(item);
  }
  return itemsByPkg;
}
async function loadBrickModule(type, pkgId, item) {
  const moduleName = `${type === "processors" || type === "editors" ? `./${type}/` : "./"}${type === "editors" ? item.fullName : item.lastName}`;
  const start = performance.now();
  try {
    await loadSharedModule(pkgId, moduleName);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);
    const cost = Math.round(performance.now() - start);
    throw new BrickLoadError(`Load ${type} of "${item.fullName}" failed${
    // istanbul ignore next
    process.env.NODE_ENV === "test" ? "" : ` (${cost > 1000 ? `${+(cost / 1000).toFixed(2)}s` : `${cost}ms`})`}: ${error === null || error === void 0 ? void 0 : error.message}`);
  }
}
function loadRestBricks(type, pkgs, itemsMap) {
  return pkgs.map(async pkg => {
    var _window$PUBLIC_ROOT;
    await loadScript(pkg.filePath, (_window$PUBLIC_ROOT = window.PUBLIC_ROOT) !== null && _window$PUBLIC_ROOT !== void 0 ? _window$PUBLIC_ROOT : "");
    await waitBasicPkg;
    return Promise.all(
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    itemsMap.get(pkg).map(item => loadBrickModule(type, pkg.id, item)));
  });
}
async function enqueueStableLoad(type, list, brickPackages,
// Post loading process hook with all v2 bricks including dependencies
postProcessV2Bricks) {
  const brickPackagesMap = new Map();
  for (const pkg of brickPackages) {
    const pkgId = pkg.id || getPkgIdByFilePath(pkg.filePath);
    brickPackagesMap.set(pkgId, pkg);
  }
  const itemsByPkg = getItemsByPkg(type, list, brickPackagesMap);
  let foundBasicPkg;
  const v2Packages = [];
  const v3PackagesOtherThanBasic = [];
  let maybeV2Adapter;
  const isInV2Container = isRunningInV2Container();
  for (const pkg of itemsByPkg.keys()) {
    if (pkg.id) {
      if (pkg.id === "bricks/basic") {
        foundBasicPkg = pkg;
      } else {
        v3PackagesOtherThanBasic.push(pkg);
      }
    } else {
      // Brick packages of v2 has no `id`
      v2Packages.push(pkg);
      if (!isInV2Container) {
        maybeV2Adapter = brickPackagesMap.get("bricks/v2-adapter");
        if (!maybeV2Adapter) {
          throw new Error("Using v2 bricks, but v2-adapter not found");
        }
      }
    }
  }
  const v2Adapter = maybeV2Adapter;
  let basicPkgPromise;
  const basicPkg = foundBasicPkg;
  if (basicPkg) {
    var _window$PUBLIC_ROOT2;
    const tempPromise = loadScript(basicPkg.filePath, (_window$PUBLIC_ROOT2 = window.PUBLIC_ROOT) !== null && _window$PUBLIC_ROOT2 !== void 0 ? _window$PUBLIC_ROOT2 : "");
    // Packages other than BASIC will wait for an extra micro-task tick.
    if (!basicPkgWillBeResolved) {
      basicPkgWillBeResolved = true;
      tempPromise.then(() => Promise.resolve()).then(resolveBasicPkg, rejectBasicPkg);
    }
    basicPkgPromise = tempPromise.then(() => Promise.all(
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    itemsByPkg.get(basicPkg).map(item => loadBrickModule(type, basicPkg.id, item))));
  }
  const pkgPromises = [basicPkgPromise].concat(loadRestBricks(type, v3PackagesOtherThanBasic, itemsByPkg));
  if (v2Adapter && v2Packages.length > 0 && !isInV2Container) {
    if (!v2AdapterPromise) {
      // Load `v2-adapter.load-bricks` and its dependencies
      const v2AdapterItemsByPkg = getItemsByPkg("bricks", [V2_ADAPTER_LOAD_BRICKS], brickPackagesMap);
      const v2AdapterPackages = [...v2AdapterItemsByPkg.keys()];
      const loadV2Adapter = async () => {
        await Promise.all(loadRestBricks("bricks", v2AdapterPackages, v2AdapterItemsByPkg));
        return document.createElement(V2_ADAPTER_LOAD_BRICKS);
      };
      v2AdapterPromise = loadV2Adapter();
    }
    pkgPromises.push(v2AdapterPromise.then(adapter => Promise.all(v2Packages.map(pkg => {
      var _pkg$propertyEditorsJ;
      const pkgId = getPkgIdByFilePath(pkg.filePath);
      const pkgNamespace = pkgId.split("/")[1];
      return adapter.resolve(v2Adapter.filePath, type === "editors" ? (_pkg$propertyEditorsJ = pkg.propertyEditorsJsFilePath) !== null && _pkg$propertyEditorsJ !== void 0 ? _pkg$propertyEditorsJ : pkg.filePath : pkg.filePath, type === "bricks" ?
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      itemsByPkg.get(pkg).map(item => `${pkgNamespace}.${item.lastName}`) : [], pkg.dll,
      // Todo: remove `brickPackages` as an argument
      brickPackages);
    }))));
  }
  if (postProcessV2Bricks && v2Packages.length > 0 && isInV2Container) {
    pkgPromises.push(postProcessV2Bricks(v2Packages.map(pkg => itemsByPkg.get(pkg).map(item => item.fullName)).flat()));
  }
  await Promise.all(pkgPromises);
}
async function dispatchRequestStatus(promise) {
  window.dispatchEvent(new Event("request.start"));
  try {
    await promise;
  } finally {
    window.dispatchEvent(new Event("request.end"));
  }
}
function getProcessorPackageName(camelPackageName) {
  return camelPackageName.replace(/[A-Z]/g, match => `-${match[0].toLocaleLowerCase()}`).replace(/_[0-9]/g, match => `-${match[1]}`);
}
function getPkgIdByFilePath(filePath) {
  return filePath.split("/").slice(0, 2).join("/");
}
function isRunningInV2Container() {
  var _window$BRICK_NEXT_VE;
  const {
    dll
  } = window;
  if (dll && (_window$BRICK_NEXT_VE = window.BRICK_NEXT_VERSIONS) !== null && _window$BRICK_NEXT_VE !== void 0 && (_window$BRICK_NEXT_VE = _window$BRICK_NEXT_VE["brick-container"]) !== null && _window$BRICK_NEXT_VE !== void 0 && _window$BRICK_NEXT_VE.startsWith("2.")) {
    return true;
  }
  return false;
}
//# sourceMappingURL=stableLoadBricks.js.map